import { equals } from './equality.utils';
import { validateRawSlateUpdates } from './validation.utils';
const removeUndefinedKeys = (obj) => {
    const newObj = {};
    Object.assign(newObj, obj);
    Object.keys(newObj).forEach(key => !newObj[key] && delete newObj[key]);
    if (Object.keys(newObj).length === 0)
        return null;
    return newObj;
};
const objectToJson = (object) => {
    if (typeof object === 'string')
        return object;
    if (!object || Object.keys(object).length <= 0)
        return undefined;
    try {
        return JSON.stringify(object);
    }
    catch (_a) {
        return undefined;
    }
};
const arrayToJson = (object) => {
    if (typeof object === 'string')
        return object;
    if (!object || Object.length <= 0)
        return undefined;
    try {
        return JSON.stringify(object);
    }
    catch (_a) {
        return undefined;
    }
};
const jsonToObject = (json) => {
    if (!json)
        return {};
    if (typeof json !== 'string')
        return json;
    try {
        return JSON.parse(json);
    }
    catch (_a) {
        return {};
    }
};
const jsonToArray = (json) => {
    if (!json)
        return [];
    if (typeof json !== 'string')
        return json;
    try {
        return JSON.parse(json);
    }
    catch (_a) {
        return [];
    }
};
export const rawBlockToDto = (rawBlock) => {
    if (!rawBlock)
        return null;
    return Object.assign(Object.assign({}, rawBlock), { props: objectToJson(rawBlock.props), extraProps: objectToJson(rawBlock.extraProps), children: arrayToJson(rawBlock.children), output: objectToJson(rawBlock.output) });
};
export const blockDtoToRaw = (block) => {
    if (!block)
        return null;
    return Object.assign(Object.assign({}, block), { props: jsonToObject(block.props), extraProps: jsonToObject(block.extraProps), children: jsonToArray(block.children), output: jsonToObject(block.output) });
};
export const rawBlockUpdatesToDto = (rawBlockUpdates) => {
    const result = rawBlockToDto(rawBlockUpdates);
    return removeUndefinedKeys(result);
};
export const blockUpdatesDtoToRaw = (blockUpdates) => {
    if (!blockUpdates)
        return null;
    return Object.assign(Object.assign({}, blockUpdates), { props: blockUpdates.props ? jsonToObject(blockUpdates.props) : undefined, extraProps: blockUpdates.extraProps
            ? jsonToObject(blockUpdates.extraProps)
            : undefined, children: blockUpdates.children
            ? jsonToArray(blockUpdates.children)
            : undefined, output: blockUpdates.output ? jsonToObject(blockUpdates.output) : undefined });
};
export const rawSlateToDto = (rawSlate) => {
    if (!rawSlate)
        return null;
    return Object.assign(Object.assign({}, rawSlate), { blocks: rawSlate.blocks.map(c => rawBlockToDto(c)) });
};
export const slateDtoToRaw = (slate) => {
    if (!slate)
        return null;
    return Object.assign(Object.assign({}, slate), { blocks: slate.blocks.map(c => blockDtoToRaw(c)) });
};
export const rawSlateUpdatesToDto = (rawSlateUpdates) => {
    var _a, _b;
    if (!rawSlateUpdates)
        return null;
    const result = Object.assign(Object.assign({}, rawSlateUpdates), { updatedBlocks: (_a = rawSlateUpdates.updatedBlocks) === null || _a === void 0 ? void 0 : _a.map(c => rawBlockUpdatesToDto(c)), addedBlocks: (_b = rawSlateUpdates.addedBlocks) === null || _b === void 0 ? void 0 : _b.map(c => rawBlockToDto(c)) });
    return removeUndefinedKeys(result);
};
export const slateUpdatesDtoToRaw = (slateUpdates) => {
    var _a, _b;
    if (!slateUpdates)
        return null;
    const result = Object.assign(Object.assign({}, slateUpdates), { updatedBlocks: (_a = slateUpdates.updatedBlocks) === null || _a === void 0 ? void 0 : _a.map(c => blockUpdatesDtoToRaw(c)), addedBlocks: (_b = slateUpdates.addedBlocks) === null || _b === void 0 ? void 0 : _b.map(c => blockDtoToRaw(c)) });
    return removeUndefinedKeys(result);
};
export const applyRawSlateUpdates = (slate, updates, validate = true) => {
    if (!slate)
        return null;
    if (!updates)
        return slate;
    if (validate)
        validateRawSlateUpdates(slate, updates);
    const { rootBlock, updatedBlocks = [], addedBlocks = [], removedBlocks = [], } = updates;
    const updatedBlocksById = updatedBlocks.reduce((pre, value) => (Object.assign(Object.assign({}, pre), { [value.id]: value })), {});
    const newRootBlock = rootBlock || slate.rootBlock;
    const newBlocks = [
        ...slate.blocks.map(block => {
            const { children, output, props, extraProps } = updatedBlocksById[block.id] || {};
            return {
                id: block.id,
                name: block.name,
                children: typeof children !== 'undefined' ? children : block.children,
                props: typeof props !== 'undefined' ? props : block.props,
                extraProps: typeof extraProps !== 'undefined' ? extraProps : block.extraProps,
                output: typeof output !== 'undefined' ? output : block.output,
            };
        }),
        ...addedBlocks,
    ]
        .filter(block => !removedBlocks.includes(block.id))
        .map(block => (Object.assign(Object.assign({}, block), { children: block.children
            ? block.children.filter(id => !removedBlocks.includes(id))
            : undefined })));
    const newBlocksById = newBlocks.reduce((pre, value) => (Object.assign(Object.assign({}, pre), { [value.id]: value })), {});
    let activeBlocks = [newRootBlock];
    let index = 0;
    while (index < activeBlocks.length) {
        const blockId = activeBlocks[index];
        index += 1;
        const { children = [] } = newBlocksById[blockId] || {};
        activeBlocks = activeBlocks.concat(children.filter(childId => !activeBlocks.includes(childId)));
    }
    const activateBlockSet = new Set(activeBlocks);
    return Object.assign(Object.assign({}, slate), { rootBlock: newRootBlock, blocks: newBlocks.filter(block => {
            const isActive = activateBlockSet.has(block.id);
            if (isActive) {
                activateBlockSet.delete(block.id);
            }
            return isActive;
        }) });
};
export const mergeSlateUpdates = (slate, oldChanges, newChanges) => {
    const { rootBlock: oldRootBlock = undefined, updatedBlocks: oldUpdatedBlocks = [], addedBlocks: oldAddedBlocks = [], removedBlocks: oldRemovedBlocks = [], } = oldChanges;
    const { rootBlock: newRootBlock = undefined, updatedBlocks: newUpdatedBlocks = [], addedBlocks: newAddedBlocks = [], removedBlocks: newRemovedBlocks = [], } = newChanges;
    const originalBlocks = slate.blocks.reduce((pre, value) => (Object.assign(Object.assign({}, pre), { [value.id]: value })), {});
    const finalUpdatedBlocks = oldUpdatedBlocks;
    const oldUpdatedBlockIds = oldUpdatedBlocks.map(block => block.id);
    const oldAddedBlockIds = oldAddedBlocks.map(block => block.id);
    newUpdatedBlocks.forEach(block => {
        if (oldUpdatedBlockIds.includes(block.id)) {
            const index = oldUpdatedBlockIds.indexOf(block.id);
            finalUpdatedBlocks[index] = Object.assign(Object.assign({}, finalUpdatedBlocks[index]), block);
        }
        else if (oldAddedBlockIds.includes(block.id)) {
            const index = oldAddedBlockIds.indexOf(block.id);
            oldAddedBlocks[index] = Object.assign(Object.assign({}, oldAddedBlocks[index]), block);
        }
        else {
            finalUpdatedBlocks.push(block);
        }
    });
    const removedIds = new Set([...oldRemovedBlocks, ...newRemovedBlocks]);
    const rootBlock = newRootBlock || oldRootBlock;
    const updatedBlocks = finalUpdatedBlocks
        .filter(b => !removedIds.has(b.id))
        .filter(b => !equals(originalBlocks[b.id], Object.assign(Object.assign({}, originalBlocks[b.id]), b)));
    const addedBlocks = [...oldAddedBlocks, ...newAddedBlocks].filter(b => !removedIds.has(b.id) && !originalBlocks[b.id]);
    const removedBlocks = [...removedIds].filter(id => !!originalBlocks[id]);
    return {
        rootBlock: slate.rootBlock !== rootBlock ? rootBlock : undefined,
        updatedBlocks: updatedBlocks.length > 0 ? updatedBlocks : undefined,
        addedBlocks: addedBlocks.length > 0 ? addedBlocks : undefined,
        removedBlocks: removedBlocks.length > 0 ? removedBlocks : undefined,
    };
};
//# sourceMappingURL=dto.utils.js.map